import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "messages", "sendButton", "preview", "previewContent", "productsPreview", "applyButton"]
  static values = {
    refineUrl: String,
    applyUrl: String
  }

  connect() {
    this.conversationHistory = []
    this.currentSummary = null
    this.currentClarifyingQuestions = null
    this.currentProductIds = null
    this.isLoading = false
  }

  async send(event) {
    event.preventDefault()

    const feedback = this.inputTarget.value.trim()
    if (!feedback || this.isLoading) return

    this.isLoading = true
    this.sendButtonTarget.disabled = true
    this.inputTarget.disabled = true

    // Add user message to chat
    this.addMessage("user", feedback)
    this.inputTarget.value = ""

    // Add to conversation history
    this.conversationHistory.push({ role: "user", content: feedback })

    // Show loading indicator
    const loadingId = this.addMessage("assistant", "Thinking...", true)

    try {
      const response = await fetch(this.refineUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          feedback: feedback,
          conversation_history: this.conversationHistory.slice(0, -1) // Exclude the message we just added
        })
      })

      const data = await response.json()

      // Remove loading indicator
      this.removeMessage(loadingId)

      if (data.success) {
        // Add AI response
        this.addMessage("assistant", data.explanation)
        this.conversationHistory.push({ role: "assistant", content: data.explanation })

        // Store the refined content
        this.currentSummary = data.summary
        this.currentClarifyingQuestions = data.clarifying_questions
        this.currentProductIds = data.product_ids

        // Show preview with summary and products
        this.showPreview(data.summary_html, data.products_html)
      } else {
        this.addMessage("assistant", `Error: ${data.error}`, false, true)
      }
    } catch (error) {
      this.removeMessage(loadingId)
      this.addMessage("assistant", `Error: ${error.message}`, false, true)
    } finally {
      this.isLoading = false
      this.sendButtonTarget.disabled = false
      this.inputTarget.disabled = false
      this.inputTarget.focus()
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.send(event)
    }
  }

  addMessage(role, content, isLoading = false, isError = false) {
    const id = `msg-${Date.now()}`
    const messageHtml = this.buildMessageHtml(id, role, content, isLoading, isError)
    this.messagesTarget.insertAdjacentHTML("beforeend", messageHtml)
    this.scrollToBottom()
    return id
  }

  removeMessage(id) {
    const element = document.getElementById(id)
    if (element) element.remove()
  }

  buildMessageHtml(id, role, content, isLoading, isError) {
    const isUser = role === "user"
    const bgClass = isUser
      ? "bg-blue-100 dark:bg-blue-900/30"
      : isError
        ? "bg-red-100 dark:bg-red-900/30"
        : "bg-gray-100 dark:bg-neutral-700"
    const textClass = isError ? "text-red-800 dark:text-red-300" : "text-gray-800 dark:text-neutral-200"
    const alignClass = isUser ? "ml-8" : "mr-8"
    const label = isUser ? "You" : "AI Assistant"

    return `
      <div id="${id}" class="p-3 rounded-lg ${bgClass} ${alignClass}">
        <div class="text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">${label}</div>
        <div class="${textClass} text-sm whitespace-pre-wrap ${isLoading ? 'animate-pulse' : ''}">${this.escapeHtml(content)}</div>
      </div>
    `
  }

  showPreview(summaryHtml, productsHtml) {
    this.previewContentTarget.innerHTML = summaryHtml + (productsHtml || "")
    this.previewTarget.classList.remove("hidden")
    this.applyButtonTarget.disabled = false
  }

  hidePreview() {
    this.previewTarget.classList.add("hidden")
    this.applyButtonTarget.disabled = true
  }

  async apply(event) {
    event.preventDefault()

    if (!this.currentSummary) {
      alert("No changes to apply")
      return
    }

    this.applyButtonTarget.disabled = true
    this.applyButtonTarget.textContent = "Applying..."

    try {
      const response = await fetch(this.applyUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          summary: this.currentSummary,
          clarifying_questions: this.currentClarifyingQuestions,
          product_ids: this.currentProductIds
        })
      })

      const data = await response.json()

      if (data.success) {
        // Reload the page to show updated content
        window.location.reload()
      } else {
        alert(`Failed to apply changes: ${data.error}`)
        this.applyButtonTarget.disabled = false
        this.applyButtonTarget.textContent = "Apply Changes"
      }
    } catch (error) {
      alert(`Error: ${error.message}`)
      this.applyButtonTarget.disabled = false
      this.applyButtonTarget.textContent = "Apply Changes"
    }
  }

  discard(event) {
    event.preventDefault()
    this.hidePreview()
    this.currentSummary = null
    this.currentClarifyingQuestions = null
    this.currentProductIds = null
    this.addMessage("assistant", "Changes discarded. Feel free to provide more feedback.")
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
